import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import {
    IChatErrorEvent,
    IChatHistoryEvent,
    IChatMessageEvent,
    IConnectCommand,
    IConversationCommand,
    IConversationEvent,
    IConversationInfoEvent,
    IDisconnectCommand,
    IMessageCommand,
    IParticipantTypingEvent,
    ISuggestedReplyEvent,
    ITransferToAgentCommand,
    ITypingCommand,
} from '@dxp/core/models';
import { environment } from '@dxp/core/environment';
import { CommunicationHubService } from '../communication.hub.service';
import { RETRY_INTERVAL_MS, RETRY_LIMIT } from '../../signalr.hub.service';

@Injectable({
    providedIn: 'root',
})
export class ChatHubService extends CommunicationHubService {
    readonly #chatEndedSubject = new Subject<IConversationEvent>();
    readonly #chatHistorySubject = new Subject<IChatHistoryEvent>();
    readonly #chatMessageSubject = new Subject<IChatMessageEvent>();
    readonly #conversationInfoSubject = new Subject<IConversationInfoEvent>();
    readonly #errorSubject = new Subject<IChatErrorEvent>();
    readonly #participantTypingSubject = new Subject<IParticipantTypingEvent>();
    readonly #suggestedReplies = new Subject<ISuggestedReplyEvent>();

    chatEnded$ = this.#chatEndedSubject.asObservable();
    chatHistory$ = this.#chatHistorySubject.asObservable();
    chatMessage$ = this.#chatMessageSubject.asObservable();
    conversationInfo$ = this.#conversationInfoSubject.asObservable();
    error$ = this.#errorSubject.asObservable();
    participantTyping$ = this.#participantTypingSubject.asObservable();
    suggestedReplies$ = this.#suggestedReplies.asObservable();

    name = 'Chat';
    url = `${environment.apiBaseUrl}/chat/hub`;

    accept(command: IConversationCommand) {
        this.send('Accept', command);
    }

    connect(command: IConnectCommand) {
        this.send('Connect', command);
    }

    peekOrConnect(command: IConversationCommand) {
        this.send('PeekOrConnect', command);
    }

    reject(command: IConversationCommand) {
        this.send('Reject', command);
    }

    sendDisconnect(command: IDisconnectCommand) {
        this.send('Disconnect', command);
    }

    sendMessage(command: IMessageCommand) {
        this.send('SendMessage', command);
    }

    initiateTransferToAgent(command: ITransferToAgentCommand) {
        this.send('InitiateTransfer', command);
    }

    cancelTransfer(command: IConversationCommand) {
        this.send('CancelTransfer', command);
    }

    sendTyping(command: ITypingCommand) {
        this.send('SendIsTyping', command);
    }

    protected override registerHandlers() {
        this.hubConnection.on(
            'ReceiveChatHistory',
            (chatHistory: IChatHistoryEvent) => {
                this.messageReceived();

                this.logger.debug(
                    'Chat Hub Service (ReceiveChatHistory) ->',
                    chatHistory,
                );
                this.logger.table(chatHistory);

                this.#chatHistorySubject.next(chatHistory);
            },
        );

        this.hubConnection.on(
            'ReceiveChatMesssage',
            (chatMessage: IChatMessageEvent) => {
                this.messageReceived();

                this.logger.debug(
                    'Chat Hub Service (ReceiveChatMesssage) ->',
                    chatMessage,
                );
                this.logger.table(chatMessage);

                this.#chatMessageSubject.next(chatMessage);
            },
        );

        this.hubConnection.on(
            'ReceiveChatMessage',
            (chatMessage: IChatMessageEvent) => {
                this.messageReceived();

                this.logger.debug(
                    'Chat Hub Service (ReceiveChatMessage) ->',
                    chatMessage,
                );
                this.logger.table(chatMessage);

                this.#chatMessageSubject.next(chatMessage);
            },
        );

        this.hubConnection.on(
            'OnParticipantTyping',
            (participantTyping: IParticipantTypingEvent) => {
                this.messageReceived();

                this.logger.debug(
                    'Chat Hub Service (OnParticipantTyping) ->',
                    participantTyping,
                );
                this.logger.table(participantTyping);

                this.#participantTypingSubject.next(participantTyping);
            },
        );

        this.hubConnection.on(
            'ReceiveChatEnded',
            (receiveChatEnded: IConversationEvent) => {
                this.messageReceived();

                this.logger.debug(
                    'Chat Hub Service (ReceiveChatEnded) ->',
                    receiveChatEnded,
                );
                this.logger.table(receiveChatEnded);

                this.#chatEndedSubject.next(receiveChatEnded);
            },
        );

        this.hubConnection.on(
            'ReceiveConversationInfo',
            (receiveConversationInfo: IConversationInfoEvent) => {
                this.messageReceived();

                this.logger.debug(
                    'Chat Hub Service (ReceiveConversationInfo) ->',
                    receiveConversationInfo,
                );
                this.logger.table(receiveConversationInfo);

                this.#conversationInfoSubject.next(receiveConversationInfo);
            },
        );

        this.hubConnection.on(
            'ReceiveSuggestedReplies',
            (receiveSuggestedReplies: ISuggestedReplyEvent) => {
                this.messageReceived();

                this.logger.debug(
                    'Chat Hub Service (ReceiveSuggestedReplies) ->',
                    receiveSuggestedReplies,
                );
                this.logger.table(receiveSuggestedReplies);

                this.#suggestedReplies.next(receiveSuggestedReplies);
            },
        );

        this.hubConnection.on(
            'ReceiveError',
            (receiveError: IChatErrorEvent) => {
                this.messageReceived();

                this.logger.error(
                    'Chat Hub Service (ReceiveError) ->',
                    receiveError,
                );
                this.logger.table(receiveError);

                const { conversationId, errorReason } = receiveError;

                if (errorReason === 'AgentNotFoundInConversation') {
                    this.tryReconnectToConversation(
                        conversationId,
                        () =>
                            this.peekOrConnect({
                                conversationId,
                            }),
                        { delayMs: RETRY_INTERVAL_MS, maxRetries: RETRY_LIMIT },
                        () => {
                            this.logger.warn(
                                `Max retries reached for ${conversationId}`,
                            );
                        },
                    );
                }

                this.#errorSubject.next(receiveError);
            },
        );

        this.hubConnection.on(
            'RequestRefresh',
            (requestRefresh: IConversationEvent) => {
                this.messageReceived();

                this.logger.debug(
                    'Chat Hub Service (RequestRefresh) ->',
                    requestRefresh,
                );
                this.logger.table(requestRefresh);

                this.peekOrConnect(requestRefresh);
            },
        );
    }

    protected override unregisterHandlers() {
        this.hubConnection.off('ReceiveChatHistory');
        this.hubConnection.off('ReceiveChatMesssage');
        this.hubConnection.off('ReceiveChatMessage');
        this.hubConnection.off('OnParticipantTyping');
        this.hubConnection.off('ReceiveChatEnded');
        this.hubConnection.off('ReceiveConversationInfo');
        this.hubConnection.off('ReceiveSuggestedReplies');
        this.hubConnection.off('ReceiveError');
        this.hubConnection.off('RequestRefresh');
    }
}
